/**
 * Completed topics — which lessons the user has heard through, kept purely
 * locally in IndexedDB. No backend contract, exactly like favorites and the
 * dictionary.
 *
 * The `topics_done` store holds `{ id, doneAt }` rows and is the **only** record
 * of completion: nothing is flagged on the `topics_head` row any more. That is the
 * point of the split — the head row belongs to the API (a content sync rewrites
 * it), this store belongs to the user, so a sync can't lose a check and a backup
 * can hand it all back (`study/userData.js`). Chapter percentages are derived from
 * here on read (`study/progress.js`), never stored.
 *
 * `doneAt` is the one field beyond the id: it costs nothing, and it is what a
 * restored backup needs to still mean "finished on that day" (and what any future
 * "recently finished" list would read).
 *
 * A row whose topic is gone (pruned by a sync, cache cleared) is harmless and left
 * alone — same tolerance as a favorite without a head row.
 */

import { STORES, get, getAll, put, remove } from '../db/idb.js';

/** Has this topic been completed? */
export async function isTopicDone(topicId) {
  return Boolean(await get(STORES.topicsDone, topicId));
}

/**
 * Record a topic as completed.
 *
 * Idempotent, and it has to be: a finished topic can be played through again any
 * number of times. A re-completion is a no-op rather than a fresh `doneAt` — the
 * date that matters is the first one.
 *
 * @param {string} topicId Topic id.
 * @returns {Promise<boolean>} `true` when this call is what marked it, `false`
 *   when it already was.
 */
export async function markTopicDone(topicId) {
  if (await isTopicDone(topicId)) return false;
  await put(STORES.topicsDone, { id: topicId, doneAt: Date.now() });
  return true;
}

/**
 * Drop a topic's completion (idempotent).
 *
 * No screen offers this yet — it exists because the store's API should be
 * symmetric, and because a restore has to be able to undo one.
 */
export function removeTopicDone(topicId) {
  return remove(STORES.topicsDone, topicId);
}

/**
 * Every completed topic id, as a Set.
 *
 * The shape the lists want: a chapter's topic list and the favorites list each
 * render many rows and ask "is this one done?" per row, so they read this once and
 * test against it instead of hitting the store per row. Cheap to read whole —
 * these are id rows with no binaries.
 */
export async function getDoneTopicIds() {
  const rows = await getAll(STORES.topicsDone);
  return new Set(rows.map((row) => row.id));
}
